import React, {
    useEffect,
    useRef,
    useState
} from 'react';
import {
    View,
    Text,
    FlatList,
    TouchableOpacity,
    Alert,
    Keyboard
} from 'react-native-web';
import PropTypes from 'prop-types';
import moment from 'moment';
import FeatherIcon from 'react-native-vector-icons/Feather';
import * as Animatable from 'react-native-animatable';

// Custom Components
import { FloatingButton } from '../../components/component_floating_button';
import {
    Button,
    NavBar,
    TextBox,
    InScreenModal
} from '../../components';

// Assets
import { ReactComponent as ProposalSVG } from '../../images/image_saved_proposals.svg';

// Utils
import { showAlert, showAppError } from '../../utils/util_helpers';
import { getFileName, getUrlExtension } from '../../utils/util_files';

// APIs
import {
    deleteSavedProposal,
    getSavedProposals,
    logSentProposal,
    saveNewProposal,
    updateSavedProposal
} from '../../modules/api_proposals';

// Configs
import GLOBAL from '../../configs/config_global';
import { cloneDeep } from 'lodash';
import { getCuisineText } from '../../utils/util_truck_helpers';
import { STATE_KEYS, useGlobalState } from '../../configs/config_state';
import { getCateringMenuSampleFiles } from '../../modules';
import SectionContainer from '../ProfileEdit/component_section_container';
import { ScrollView } from 'react-native-web';
import LoadingView from '../../components/component_loading_view';

export default function ProposalTab(props) {
    const { cateringData, onCloseRequested, onProposalTabRequested } = props;

    // region Refs
    const txtName = useRef();
    const txtIntroduction = useRef();
    const txtPersonalization = useRef();
    const txtDescription = useRef();
    const txtRequirements = useRef();
    const txtClosing = useRef();
    const mainScrollView = useRef();
    const proposalFileView = useRef();
    const showCateringMenuSamples = useRef(true);
    // endregion

    // region Global State
    const [globalTruckData] = useGlobalState(STATE_KEYS.TRUCK_DATA);
    const [globalMenuFiles] = useGlobalState(STATE_KEYS.CATERING_MENU_SAMPLES);
    // endregion

    // region Local States
    const [isLoading, setLoading] = useState(false);
    const [proposalList, setProposalList] = useState([]);
    const [showProposalModal, setShowProposalModal] = useState(false);
    const [selectedProposalData, setSelectedProposalData] = useState(null);

    const [proposalName, setProposalName] = useState('');
    const [proposalIntroduction, setProposalIntroduction] = useState('');
    const [proposalIntroductionFocused, setProposalIntroductionFocused] = useState(false);
    const [proposalPersonalization, setProposalPersonalization] = useState('');
    const [proposalPersonalizationFocused, setProposalPersonalizationFocused] = useState(false);
    const [proposalDescription, setProposalDescription] = useState('');
    const [proposalDescriptionFocused, setProposalDescriptionFocused] = useState(false);
    const [proposalRequirements, setProposalRequirements] = useState('');
    const [proposalRequirementsFocused, setProposalRequirementsFocused] = useState(false);
    const [proposalClosing, setProposalClosing] = useState('');
    const [proposalClosingFocused, setProposalClosingFocused] = useState(false);
    const [proposalFiles, setProposalFiles] = useState([]);
    const [proposalLoading, setProposalLoading] = useState(false);
    const [cateringSampleFiles, setCateringSampleFiles] = useState([]);
    const [cateringSampleLoading, setCateringSampleLoading] = useState(false);

    const [emailJustSent, setEmailJustSent] = useState(false);

    // endregion

    // region Component Mounting
    useEffect(() => {
        goFetchSavedProposals();
        goFetchSampleMenuFiles();
    }, []);
    // endregion

    // region Function - Go Fetch Sample Menu Files
    const goFetchSampleMenuFiles = () => {
        if (globalMenuFiles?.loaded) {
            setCateringSampleFiles(globalMenuFiles.data || []);
        }
        else {
            const cateringSampleAlbumID = globalTruckData?.albums?.find((item) => item.name.toLowerCase() === 'catering')?.id;
            if (cateringSampleAlbumID) {
                setCateringSampleLoading(true);
                getCateringMenuSampleFiles(globalTruckData?.id, cateringSampleAlbumID)
                    .then((result) => setCateringSampleFiles(result?.data || []))
                    .catch((error) => showAppError(error))
                    .finally(() => setCateringSampleLoading(false));
            }
        }
    };
    // endregion

    // region Function - Fetch Saved Proposal List
    const goFetchSavedProposals = () => {
        setLoading(true);
        getSavedProposals(globalTruckData?.id)
            .then((result) => setProposalList(result.data))
            .catch((error) => {
                showAppError(error);
                setProposalList([]);
            })
            .finally(() => setLoading(false));
    };
    // endregion

    // region Function - Save Proposal
    const goSaveProposal = () => {
        Keyboard.dismiss();

        if (!proposalName?.trim() || !proposalIntroduction?.trim() || !proposalDescription?.trim() || !proposalClosing?.trim()) {
            showAlert({ text: 'Please fill in all required information (marked with red asterisks) before proceeding' });
            return;
        }

        setProposalLoading(true);
        const savingData = {
            name: proposalName,
            introduction: proposalIntroduction,
            personalization: proposalPersonalization,
            proposal: proposalDescription,
            other_requirements: proposalRequirements,
            closing: proposalClosing,
            catering_menu_items: proposalFiles
        };
        if (selectedProposalData) {
            console.log('sending data', savingData);
            updateSavedProposal(globalTruckData?.id, selectedProposalData.id, savingData)
                .then((newData) => {
                    const oldIndex = proposalList.findIndex((proposal) => proposal.id === selectedProposalData.id);
                    const newArr = cloneDeep(proposalList);
                    newArr.splice(oldIndex, 1, newData);
                    setProposalList(newArr);
                    showAlert({ text: 'Successfully updated proposal template' });
                    setShowProposalModal(false);
                })
                .catch((error) => showAppError(error))
                .finally(() => setProposalLoading(false));
        }
        else {
            saveNewProposal(globalTruckData?.id, savingData)
                .then((newData) => {
                    setProposalList(proposalList.concat(newData));
                    showAlert({ text: 'Successfully saved new proposal template' });
                    setShowProposalModal(false);
                })
                .catch((error) => showAppError(error))
                .finally(() => setProposalLoading(false));
        }
    };
    // endregion

    // region Function - Start Create New Proposal
    const startCreateNewProposal = () => {
        if (cateringData) {
            onProposalTabRequested();
            return;
        }

        if (proposalList.length < 5) {
            setSelectedProposalData(null);
            setProposalName('');
            setProposalIntroduction(`Thank you for your interest in my food truck, I’m excited for the opportunity to work with you. We're passionate about ${getCuisineText(globalTruckData, 5)} cuisine.\n[ENTER IN WHAT MAKES YOUR TRUCK UNIQUE OR SPECIAL]`);
            setProposalIntroductionFocused(false);
            setProposalPersonalization('');
            setProposalPersonalizationFocused(false);
            setProposalDescription(`Attached is my catering menu or you can check out my Truckster profile for my standard vending menu. I’m able to do customizations and meet dietary restrictions as needed.`);
            setProposalDescriptionFocused(false);
            setProposalRequirements('');
            setProposalRequirementsFocused(false);
            setProposalClosing(`I’m available for any questions. Look forward to hearing from you soon!\n\nThanks,\n${GLOBAL.DATA.USER?.name || '[YOUR CONTACT NAME]'}`);
            setProposalClosingFocused(false);
            setProposalFiles([]);
            setShowProposalModal(true);
        }
        else Alert.alert(GLOBAL.APP_NAME, 'You can only have a maximum of 5 saved proposal templates at a time');
    };
    // endregion

    // region Function - Open Saved Proposal
    const openSavedProposal = (item) => {
        setSelectedProposalData(item);
        setProposalName(item['name']);
        setProposalIntroduction(item['introduction']);
        setProposalIntroductionFocused(false);
        setProposalPersonalization(item['personalization']);
        setProposalPersonalizationFocused(false);
        setProposalDescription(item['proposal']);
        setProposalDescriptionFocused(false);
        setProposalRequirements(item['other_requirements']);
        setProposalRequirementsFocused(false);
        setProposalClosing(item['closing']);
        setProposalClosingFocused(false);
        setProposalFiles((item['catering_menu_items'] || []).map((file) => file.id));
        setShowProposalModal(true);
    };
    // endregion

    // region Function - Delete Saved Proposal
    const goDeleteProposal = () => {
        showAlert({
            text: 'Are you sure you want to delete this saved proposal template? This action cannot be undone.',
            cancelButton: { text: 'Cancel' },
            confirmButton: {
                text: 'Confirm',
                color: 'red',
                onPress: () => {
                    setProposalLoading(true);
                    deleteSavedProposal(globalTruckData?.id, selectedProposalData.id)
                        .then(() => {
                            setProposalList(proposalList.filter((proposal) => proposal.id !== selectedProposalData.id));
                            setShowProposalModal(false);
                            showAlert({ text: 'Successfully deleted proposal template' });
                        })
                        .catch((error) => showAppError(error))
                        .finally(() => setProposalLoading(false));
                }
            }
        })
    };
    // endregion

    // region Function - Send Proposal
    const generateEmailContent = () => (
        (
            `Hi ${cateringData?.name},%0A%0A` +
            `${proposalIntroduction}%0A%0A` +
            (proposalPersonalization?.trim() ? `${proposalPersonalization}%0A%0A` : '') +
            (proposalDescription?.trim() ? `${proposalDescription}%0A%0A` : '') +
            (proposalRequirements?.trim() ? `${proposalRequirements}%0A%0A` : '') +
            (proposalClosing?.trim() ? proposalClosing : '')
        ).replace(/\n/g, '%0A')
    );

    const goSendProposal = () => {
        Keyboard.dismiss();

        showAlert({
            text: 'Please remember to attach any catering menu sample files that you would like to share with the client before sending the email out',
            cancelButton: {
                text: 'Got it',
                color: GLOBAL.COLOR.BASIC.UI_BLUE,
                onPress: () => {
                    // Show sent email overlay
                    setEmailJustSent(true);

                    // Open email client
                    window.open(`mailto:${cateringData?.email}?subject=${`Catering Proposal - ${cateringData?.occasion || ''}`}&body=${generateEmailContent()}`, '_blank');
                }
            }
        });
    };
    // endregion

    // region Render Sent Email Overlay
    const renderSentEmailOverlay = () => (
        emailJustSent &&
        <Animatable.View
            animation={'fadeZoom'} duration={600}
            style={{
                position: 'absolute', left: 0, right: 0, top: 0, bottom: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.95)', alignItems: 'center', justifyContent: 'center',
                paddingHorizontal: '25%'
            }}>
            <Text style={{ fontWeight: 'bold', marginBottom: 24, color: GLOBAL.COLOR.BASIC.UI_BLUE }}>
                Did you send your proposal to the client?
            </Text>

            <Button
                onPress={() => {
                    setShowProposalModal(false);
                    setEmailJustSent(false);
                    onCloseRequested();

                    // Log to Braze
                    logSentProposal(globalTruckData?.id, selectedProposalData?.id).catch(() => {});
                }}
                text={'I have sent the email'}
                textStyle={{ color: 'white' }}
            />

            <Button
                onPress={() => setEmailJustSent(false)}
                text={'Go back to the proposal'}
                textStyle={{ color: GLOBAL.COLOR.BASIC.BLACK_TWO }}
                style={{ marginTop: 8, backgroundColor: 'white', borderWidth: 2, borderColor: GLOBAL.COLOR.DIVIDER }}
            />
        </Animatable.View>
    );
    // endregion

    // region Render Proposal Modal
    const renderProposalModal = () => (
        <InScreenModal visible={showProposalModal} fullWidth={true} style={{ backgroundColor: GLOBAL.COLOR.BACKGROUND_GREY }}>
            {/* Nav Bar */}
            <NavBar
                title={selectedProposalData ? (cateringData ? 'Review Sending Proposal' : 'Edit Proposal') : 'New Proposal'}
                subTitle={cateringData ? `${cateringData?.occasion} | Requested by ${cateringData?.name}` : ''}
                leftButtonOnPress={proposalLoading ? null : (() => setShowProposalModal(false))}
                rightButtonTitle={cateringData ? null : (selectedProposalData ? 'Delete Proposal' : null)}
                rightButtonBackgroundColor={GLOBAL.COLOR.BASIC.RED}
                rightButtonOnPress={() => goDeleteProposal()}
                ignoreTopBarHeight={true}
            />

            {/* Sending Proposal Description */}
            {
                !!cateringData &&
                <View style={{ alignSelf: 'stretch', backgroundColor: GLOBAL.COLOR.BACKGROUND_GREY, marginVertical: 24, marginHorizontal: '20%' }}>
                    <Text style={{ color: GLOBAL.COLOR.BASIC.UI_BLUE, lineHeight: 30, textAlign: 'center', alignSelf: 'center', marginHorizontal: 24 }}>
                        <Text style={{ fontWeight: 'bold', color: GLOBAL.COLOR.BASIC.UI_BLUE }}>
                            Please carefully review the content and adjust for this client and specific occasion as needed.
                        </Text>
                        {'\n\n'}
                        Once you’re ready, preview your content below and hit Send Email to open your email draft. Make sure you choose the correct “from” email address for your business!
                        {'\n\n'}
                        <Text style={{ color: GLOBAL.COLOR.TEXT_LIGHT_GRAY }}>
                            <Text style={{ fontWeight: 'bold', color: GLOBAL.COLOR.TEXT_LIGHT_GRAY }}>Note:{' '}</Text>
                            The edits you make here won’t change your saved proposal template.
                        </Text>
                    </Text>
                </View>
            }

            {/* Content */}
            <View style={{ flexDirection: 'row', alignItems: 'flex-start', width: '100%', flex: 1 }}>
                <ScrollView
                    ref={mainScrollView.current}
                    style={{ width: '60%', height: '100%', backgroundColor: GLOBAL.COLOR.BACKGROUND_GREY }}
                    showsVerticalScrollIndicator={false}>
                    {/* Editor */}
                    <View
                        pointerEvents={proposalLoading ? 'none' : 'auto'}
                        style={{
                            alignSelf: 'stretch', backgroundColor: 'white', padding: 24,
                            borderBottomWidth: 1, borderRightWidth: 1, borderTopWidth: cateringData ? 1 : 0, borderColor: GLOBAL.COLOR.DIVIDER,
                            opacity: proposalLoading ? 0.5 : 1
                        }}>
                        {/* Name */}
                        {
                            !cateringData &&
                            <TextBox
                                ref={txtName}
                                title={'Proposal Name'}
                                placeholder={'Proposal Name | What kind of event is this for? (for your eyes only)'}
                                value={proposalName}
                                onChangeText={setProposalName}
                                onSubmitEditing={() => txtIntroduction.current?.focus()}
                                disabled={proposalLoading}
                            />
                        }

                        {/* Introduction */}
                        <TextBox
                            ref={txtIntroduction}
                            title={'Introduction'}
                            placeholder={'Introduction | What makes your truck special?'}
                            contentBoxStyle={{ height: 200 }}
                            value={proposalIntroduction}
                            onChangeText={setProposalIntroduction}
                            multiline={true}
                            onFocus={() => setProposalIntroductionFocused(true)}
                            onBlur={() => setProposalIntroductionFocused(false)}
                            disabled={proposalLoading}
                        />

                        {/* Personalization */}
                        <TextBox
                            ref={txtPersonalization}
                            title={'Personalization (Optional)'}
                            placeholder={'Personalization (Optional) | Something about their event if you want'}
                            contentBoxStyle={{ height: 200 }}
                            value={proposalPersonalization}
                            onChangeText={setProposalPersonalization}
                            multiline={true}
                            onFocus={() => setProposalPersonalizationFocused(true)}
                            onBlur={() => setProposalPersonalizationFocused(false)}
                            disabled={proposalLoading}
                        />

                        {/* Description */}
                        <TextBox
                            ref={txtDescription}
                            title={'Description'}
                            placeholder={'Description | Your proposal pitch'}
                            contentBoxStyle={{ height: 200 }}
                            value={proposalDescription}
                            onChangeText={setProposalDescription}
                            multiline={true}
                            onFocus={() => setProposalDescriptionFocused(true)}
                            onBlur={() => setProposalDescriptionFocused(false)}
                            disabled={proposalLoading}
                        />

                        {/* Requirements */}
                        <TextBox
                            ref={txtRequirements}
                            title={'Other Requirements (Optional)'}
                            placeholder={'Other Requirements (Optional) | Minimum required, deposit, agreement, cancellation policy, etc.'}
                            contentBoxStyle={{ height: 200 }}
                            value={proposalRequirements}
                            onChangeText={setProposalRequirements}
                            multiline={true}
                            onFocus={() => setProposalRequirementsFocused(true)}
                            onBlur={() => setProposalRequirementsFocused(false)}
                            disabled={proposalLoading}
                        />

                        {/* Closing */}
                        <TextBox
                            ref={txtClosing}
                            title={'Closing'}
                            placeholder={'Closing | Your closing statement and signature'}
                            contentBoxStyle={{ height: 150 }}
                            value={proposalClosing}
                            onChangeText={setProposalClosing}
                            multiline={true}
                            onFocus={() => setProposalClosingFocused(true)}
                            onBlur={() => setProposalClosingFocused(false)}
                            disabled={proposalLoading}
                        />

                        {/* Catering Menu Attachments */}
                        {
                            cateringSampleLoading ?
                                <LoadingView isLoading={true} style={{ height: 200 }} />
                                :
                                (
                                    (showCateringMenuSamples.current && !cateringData) &&
                                    <View
                                        ref={proposalFileView}
                                        style={{ alignSelf: 'stretch' }}>
                                        {/* Main Content */}
                                        <SectionContainer
                                            title={'Catering Menu Samples'}
                                            style={{ paddingLeft: 0, paddingRight: 0 }}
                                            description={'Choose from the list of your catering menu sample files to share with the client. Tap to select the files that you want to be included in the email.'}
                                            showTopBorder={false}>
                                            {
                                                cateringSampleFiles.length ?
                                                    cateringSampleFiles.map((item, index) => {
                                                        const isChosen = proposalFiles.includes(item.id);
                                                        return(
                                                            <TouchableOpacity
                                                                key={`sampleFile_${item.id}`}
                                                                onPress={() => {
                                                                    if (proposalFiles.includes(item.id)) {
                                                                        setProposalFiles(proposalFiles.filter((id) => id !== item.id));
                                                                    }
                                                                    else setProposalFiles(proposalFiles.concat(item.id));
                                                                }}
                                                                style={{
                                                                    flex: 1, flexDirection: 'row', alignItems: 'center',
                                                                    marginVertical: 6, paddingVertical: 8, paddingHorizontal: 8,
                                                                    backgroundColor: isChosen ? GLOBAL.COLOR.BASIC.UI_GREEN : 'white',
                                                                    borderRadius: 8, borderWidth: 1, borderColor: isChosen ? GLOBAL.COLOR.BASIC.UI_GREEN : 'white'
                                                                }}>
                                                                {/* Icon */}
                                                                <FeatherIcon
                                                                    name={isChosen ? 'check' : getUrlExtension(item.path) === 'pdf' ? 'file-text' : 'image'}
                                                                    color={isChosen ? 'white' : GLOBAL.COLOR.TEXT_GRAY}
                                                                    size={24}
                                                                />

                                                                {/* File Name */}
                                                                <Text style={{ color: isChosen ? 'white' : GLOBAL.COLOR.TEXT_GRAY, marginLeft: 12, flex: 1 }} numberOfLines={1}>
                                                                    {item.name ? getFileName(item.name) : `(File #${index + 1})`}
                                                                </Text>
                                                            </TouchableOpacity>
                                                        );
                                                    })
                                                    :
                                                    <Text style={{
                                                        color: GLOBAL.COLOR.TEXT_GRAY, fontSize: 14, lineHeight: 22, fontStyle: 'italic',
                                                        marginVertical: 32, alignSelf: 'stretch', marginHorizontal: 16, textAlign: 'center'
                                                    }}>
                                                        You have no catering menu sample files, you can add them from
                                                        <Text style={{ fontWeight: 'bold', color: GLOBAL.COLOR.TEXT_GRAY }}>{' '}Profile</Text>
                                                        , then
                                                        <Text style={{ fontWeight: 'bold', color: GLOBAL.COLOR.TEXT_GRAY }}>{' '}Menu</Text>
                                                        , and
                                                        <Text style={{ fontWeight: 'bold', color: GLOBAL.COLOR.TEXT_GRAY }}>{' '}Catering</Text>
                                                        .
                                                    </Text>
                                            }
                                        </SectionContainer>
                                    </View>
                                )
                        }
                    </View>

                    {/* Save Button */}
                    <Button
                        onPress={() => {
                            if (cateringData) {
                                goSendProposal();
                            }
                            else goSaveProposal();
                        }}
                        style={{ backgroundColor: GLOBAL.COLOR.BASIC.UI_BLUE, margin: 32 }}
                        textStyle={{ color: 'white', fontWeight: 'bold' }}
                        text={cateringData ? 'Send Email' : `${selectedProposalData ? 'Update' : 'Create'} Proposal Template`}
                        isLoading={proposalLoading}
                    />
                </ScrollView>

                {/* Preview Section */}
                <ScrollView
                    style={{ width: '40%', height: '100%', backgroundColor: GLOBAL.COLOR.BACKGROUND_GREY }}
                    contentContainerStyle={{ padding: 24 }}
                    showsVerticalScrollIndicator={false}>
                    <Text style={{ fontSize: 32, fontWeight: '900', textAlign: 'left', color: GLOBAL.COLOR.BASIC.BLACK_TWO, marginBottom: 32 }}>
                        Preview
                    </Text>

                    <Text style={{ fontSize: 18, color: GLOBAL.COLOR.TEXT_GRAY, lineHeight: 24 }}>
                        Hi {cateringData ? cateringData.name : `[Auto-filled by your client's name]`},{'\n\n'}
                    </Text>

                    {
                        !!proposalIntroduction?.trim() &&
                        <Text style={{ fontSize: 18, color: proposalIntroductionFocused ? 'black' : GLOBAL.COLOR.TEXT_GRAY, lineHeight: 32 }}>
                            {proposalIntroduction}{'\n\n'}
                        </Text>
                    }

                    {
                        !!proposalPersonalization?.trim() &&
                        <Text style={{ fontSize: 18, color: proposalPersonalizationFocused ? 'black' : GLOBAL.COLOR.TEXT_GRAY, lineHeight: 32 }}>
                            {proposalPersonalization}{'\n'}
                        </Text>
                    }

                    {
                        !!proposalDescription?.trim() &&
                        <Text style={{ fontSize: 18, color: proposalDescriptionFocused ? 'black' : GLOBAL.COLOR.TEXT_GRAY, lineHeight: 32 }}>
                            {proposalDescription}{'\n\n'}
                        </Text>
                    }

                    {
                        !!proposalRequirements?.trim() &&
                        <Text style={{ fontSize: 18, color: proposalRequirementsFocused ? 'black' : GLOBAL.COLOR.TEXT_GRAY, lineHeight: 32 }}>
                            {proposalRequirements}{'\n\n'}
                        </Text>
                    }

                    {
                        !!proposalClosing?.trim() &&
                        <Text style={{ fontSize: 18, color: proposalClosingFocused ? 'black' : GLOBAL.COLOR.TEXT_GRAY, lineHeight: 32 }}>
                            {proposalClosing}
                        </Text>
                    }
                </ScrollView>
            </View>

            {/* Sent Email Overlay */}
            {renderSentEmailOverlay()}
        </InScreenModal>
    );
    // endregion

    // region Render Toolbar
    const TOOLBAR_HEIGHT = 64;
    const renderToolbarItem = (icon, text, onPress = () => {}, isFirst = false) => (
        <TouchableOpacity
            onPress={onPress}
            style={{ height: TOOLBAR_HEIGHT, marginHorizontal: 24, flexDirection: 'row', alignItems: 'center' }}>
            <FeatherIcon name={icon} size={24} color={'white'} />
            <Text style={{ color: 'white', fontSize: 16, fontWeight: '500', marginLeft: 8 }}>
                {text}
            </Text>
        </TouchableOpacity>
    );

    const renderToolbar = () => (
        !cateringData &&
        <Animatable.View
            animation={showProposalModal ? 'slideOutDown' : 'slideInUp'}
            style={{
                position: 'absolute', bottom: 0, backgroundColor: 'black', borderTopLeftRadius: 8, borderTopRightRadius: 8,
                alignSelf: 'center', boxShadow: '0px 1px 5px #aeafb1, 0px -1px 5px #aeafb1',
                flexDirection: 'row', alignItems: 'center', justifyContent: 'center'
            }}>
            {renderToolbarItem('plus-circle', 'Create New Proposal Template', () => startCreateNewProposal(), true)}
        </Animatable.View>
    )
    // endregion

    // region MAIN RENDER
    return(
        <View style={{ flex: 1, width: '100%', backgroundColor: GLOBAL.COLOR.BACKGROUND_GREY }}>
            {
                (isLoading && !proposalList.length) ?
                    <LoadingView
                        style={{ backgroundColor: GLOBAL.COLOR.BACKGROUND_GREY }}
                        description={'Loading your saved proposals'}
                    />
                    :
                    <View style={{ flex: 1, width: '100%', backgroundColor: GLOBAL.COLOR.BACKGROUND_GREY }}>
                        {/* Main Content */}
                        {
                            proposalList.length > 0 ?
                                <Animatable.View
                                    animation={'fadeIn'}
                                    style={{ flex: 1, width: '100%' }}>
                                    {/* Created Proposal List */}
                                    <FlatList
                                        style={{ flex: 1, width: '100%' }}
                                        contentContainerStyle={{ paddingHorizontal: GLOBAL.IS_TABLET ? '25%' : 24, paddingTop: 24, paddingBottom: GLOBAL.BOTTOM_BAR_HEIGHT }}
                                        showsVerticalScrollIndicator={false}
                                        refreshing={isLoading}
                                        onRefresh={() => goFetchSavedProposals()}
                                        data={proposalList}
                                        renderItem={({ item }) => (
                                            <TouchableOpacity
                                                onPress={() => openSavedProposal(item)}
                                                style={{
                                                    alignSelf: 'stretch', backgroundColor: 'white', borderRadius: 8, borderColor: GLOBAL.COLOR.DIVIDER,
                                                    paddingHorizontal: 32, paddingVertical: 24, marginHorizontal: '25%'
                                                }}>
                                                <Text style={{ fontWeight: 'bold', fontSize: 20 }} numberOfLines={1}>
                                                    {item.name}
                                                </Text>

                                                <Text style={{ color: GLOBAL.COLOR.TEXT_GRAY, marginTop: 16, fontSize: 16 }} numberOfLines={1}>
                                                    {
                                                        moment.utc(item['created_at']).isBefore(moment.utc(item['updated_at']), 'date') ?
                                                            `Saved ${moment.utc(item['created_at']).format('MMM D')} | Last Edited ${moment.utc(item['updated_at']).format('MMM D')}`
                                                            :
                                                            `Saved ${moment.utc(item['created_at']).format('MMM D')}`
                                                    }
                                                </Text>
                                            </TouchableOpacity>
                                        )}
                                        keyExtractor={(item) => `${item.id}_${item['updated_at']}`}
                                        ItemSeparatorComponent={() => <View style={{ width: 1, height: 16 }}/>}
                                    />

                                    {/* New Proposal Button */}
                                    {renderToolbar()}
                                </Animatable.View>
                                :
                                <View style={{ flex: 1, width: '100%', alignItems: 'center', justifyContent: 'center', padding: 64 }}>
                                    {/* Image */}
                                    <ProposalSVG width={64} height={40 / 36 * 64} />

                                    {/* Title */}
                                    <Text style={{ fontSize: 28, fontWeight: 'bold', marginTop: 32 }}>
                                        Add your first proposal
                                    </Text>

                                    {/* Description */}
                                    <Text style={{ fontSize: 18, lineHeight: 24, color: GLOBAL.COLOR.TEXT_GRAY, marginTop: 16, textAlign: 'center' }}>
                                        Quickly build and send pre-saved emails to catering prospects, making it easy to pitch your business in just a few clicks
                                    </Text>

                                    {/* Button */}
                                    <Button
                                        onPress={startCreateNewProposal}
                                        style={{ backgroundColor: GLOBAL.COLOR.MAIN, marginTop: 32, alignSelf: 'center', width: 300 }}
                                        textStyle={{ color: 'white', fontWeight: 'bold' }}
                                        text={'New Proposal'}
                                        image={'plus'}
                                        imageSize={24}
                                        imageColor={'white'}
                                        imageStickToText={true}
                                        useFeather={true}
                                    />
                                </View>
                        }

                        {/* Proposal Modal */}
                        {renderProposalModal()}
                    </View>
            }
        </View>
    );
    // endregion
}

// Props
ProposalTab.propTypes = {
    sampleMenuFiles: PropTypes.array,
    cateringData: PropTypes.object,
    onCloseRequested: PropTypes.func,
    onProposalTabRequested: PropTypes.func
};

ProposalTab.defaultProps = {
    sampleMenuFiles: [],
    cateringData: null,
    onCloseRequested: () => {},
    onProposalTabRequested: () => {}
};
// endregion
